import buildAcrYamlModel from "./buildAcrYamlModel";
import formatCatalogLabel from "../formatCatalogLabel";
import {
  catalogCriteriaLabel,
  componentLabelForReport,
  criteriaGuidanceHref,
  DEFAULT_OPENACR_TERMS,
  getCatalogChapter,
  levelCountWithLevel,
  mapChapterIdForExport,
  pickDisplayStandards,
  reportChapterHeadingLabel,
  reportHeadingForStandard,
} from "./openAcrReportShared";
import reportFilename from "../reportFilename";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function markdownToHtml(text) {
  return String(text || "")
    .split("\n")
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
}

function anchorIconSvg() {
  return `<svg focusable="false" aria-hidden="true" class="icon-link"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"></path></svg>`;
}

function headerWithAnchor(headerText, idText, level) {
  return `<h${level} id="${escapeHtml(idText)}-download">${escapeHtml(headerText)} <a href="#${escapeHtml(idText)}-download" class="header-anchor"><span class="anchor-icon" aria-hidden="true">${anchorIconSvg()}</span> <span class="visuallyhidden">Anchor link</span></a></h${level}>`;
}

function levelLabel(level) {
  const map = {
    supports: "Supports",
    "partially-supports": "Partially Supports",
    "does-not-support": "Does Not Support",
    "not-applicable": "Not Applicable",
    "not-evaluated": "Not Evaluated",
  };
  return map[level] || "Not Applicable";
}

function standardsIncluded(catalogData, model, standardChapters) {
  const items = (standardChapters || [])
    .map((chapterId) => mapChapterIdForExport(chapterId))
    .filter((chapterId) => {
      const chapter = model.chapters?.[chapterId];
      return chapter && (!chapter.disabled || chapter.notes);
    })
    .map((chapterId) => `<li>${escapeHtml(reportChapterHeadingLabel(chapterId))}</li>`)
    .join("");
  return `<ul>${items}</ul>`;
}

function acrCss() {
  return `body#acr {
      padding-left: 35px;
    }
    h2,
    h3 {
      position: relative;
    }
    h2 a.header-anchor,
    h3 a.header-anchor {
      position: absolute;
      left: -2rem;
    }
    h2 a.header-anchor:hover svg,
    h2 a.header-anchor:focus svg,
    h2 a.header-anchor:focus-within svg,
    h3 a.header-anchor:hover svg,
    h3 a.header-anchor:focus svg,
    h3 a.header-anchor:focus-within svg {
      fill: #0000EE;
      opacity: 1;
    }
    h2 a.header-anchor svg,
    h3 a.header-anchor svg {
      width: 28px;
      height: 28px;
      opacity: .3;
    }
    @media all and (max-width: 63.99em) {
      h2 a.header-anchor,
      h3 a.header-anchor {
        position:relative;
        opacity: 1;
        left: 0;
        vertical-align: top
      }
    }
    a.header-anchor {
      opacity: 0.4;
      font-size: small;
      text-decoration: none;
      position: relative;
      -webkit-transition: opacity 1s, font-size 1s;
      -moz-transition: opacity 1s, font-size 1s;
      -o-transition: opacity 1s, font-size 1s;
      transition: opacity 1s, font-size 1s;
    }
    a.header-anchor:focus,a.header-anchor:hover {
      text-decoration: underline;
      font-size: large;
      opacity: 1
    }
    .usa-sr-only,
    .visuallyhidden {
      border: 0;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
      white-space: nowrap;
    }
    .applicable-standards-guidelines-table th:nth-child(1) { width: 40%; }
    .applicable-standards-guidelines-table th:nth-child(2) { width: 60%; }
    .component-level-count-1 .component-level-label { display: none; }
    table { border-collapse: collapse; }`;
}

export default function buildAcrHtmlString(evaluation, catalog) {
  const model = buildAcrYamlModel(evaluation, catalog);
  const catalogData = {
    ...catalog,
    lang: "en",
    title: formatCatalogLabel(catalog) || model.catalog || catalog?.title || catalog?.name || catalog?.label || "",
    standards: (catalog?.standards || []).map((standard) => ({
      ...standard,
      report_heading: reportHeadingForStandard(standard),
    })),
    terms: Array.isArray(catalog?.terms) && catalog.terms.length > 0
      ? catalog.terms
      : DEFAULT_OPENACR_TERMS,
  };
  const displayStandards = pickDisplayStandards(catalogData.standards);

  const standardsRows = displayStandards
    .map((standard) => `<tr><td><a href="${escapeHtml(standard.url || "")}" target="_blank" rel="noopener noreferrer">${escapeHtml(standard.label || standard.id)}<span class="usa-sr-only"> (opens in new window)</span></a></td><td>${standardsIncluded(catalogData, model, standard.chapters)}</td></tr>`)
    .join("");

  const standardsSections = displayStandards.map((standard) => {
    const chapterBlocks = (standard.chapters || []).map((sourceChapterId) => {
      const catalogChapter = getCatalogChapter(catalogData, sourceChapterId);
      const mappedChapterId = mapChapterIdForExport(sourceChapterId);
      const dataCategory = model.chapters?.[mappedChapterId];
      if (!catalogChapter || !dataCategory) return "";
      if (dataCategory.disabled && !dataCategory.notes) return "";
      const criteriaRows = (dataCategory.criteria || []).map((criteria) => {
        const conformanceItems = (criteria.components || [])
          .filter((component) => component?.adherence?.level)
          .map((component) => `<li><span class="component-level-label"><strong>${escapeHtml(componentLabelForReport(component.name))}</strong>: </span><p>${escapeHtml(levelLabel(component.adherence.level))}</p></li>`)
          .join("");
        const noteItems = (criteria.components || [])
          .filter((component) => component?.adherence?.notes)
          .map((component) => `<li><span class="component-level-label"><strong>${escapeHtml(componentLabelForReport(component.name))}</strong>: </span>${markdownToHtml(component.adherence.notes)}</li>`)
          .join("");
        const critHref = criteriaGuidanceHref(
          catalogData,
          sourceChapterId,
          criteria.num,
          standard.url
        );
        const critLabel = `${escapeHtml(criteria.num)} ${escapeHtml(catalogCriteriaLabel(catalogData, sourceChapterId, criteria.num))}`;
        const critCell = critHref.trim()
          ? `<a href="${escapeHtml(critHref)}" target="_blank" rel="noopener noreferrer">${critLabel}<span class="usa-sr-only"> (opens in new window)</span></a>`
          : critLabel;
        return `<tr id="${escapeHtml(criteria.num)}"><td>${critCell}</td><td><ul class="component-level-count-${levelCountWithLevel(criteria.components || [])}">${conformanceItems}</ul></td><td><ul class="component-level-count-${levelCountWithLevel(criteria.components || [])}">${noteItems}</ul></td></tr>`;
      }).join("");
      return `${headerWithAnchor(reportChapterHeadingLabel(mappedChapterId), sourceChapterId, 3)}
      ${dataCategory.notes ? `<div id="${escapeHtml(sourceChapterId)}-notes" class="chapter-notes-section">Notes: ${markdownToHtml(dataCategory.notes)}</div>` : ""}
      ${dataCategory.disabled ? "" : `<table class="usa-table" border="1" style="border-collapse: collapse"><thead><tr><th>Criteria</th><th>Conformance Level</th><th>Remarks and Explanations</th></tr></thead><tbody>${criteriaRows}</tbody></table>`}`;
    }).join("");
    return `${headerWithAnchor(standard.report_heading, standard.id, 2)}${chapterBlocks}`;
  }).join("");

  const relatedList = (model.related_acrs || [])
    .map((related) => `<li><a href="${escapeHtml(related.url)}">${escapeHtml(related.url)} (${escapeHtml(related.type)})</a></li>`)
    .join("");

  const documentTitle = model.title
    || `${model.product?.name || "Report"} Accessibility Conformance Report`;

  return `<!doctype HTML>
<html lang="${escapeHtml(catalogData.lang)}">
<head>
  <title>${escapeHtml(documentTitle)}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta charset="utf-8">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
</head>
<body id="acr">
  <style>${acrCss()}</style>
  <main>
    <div class="grid-container">
      <h1><span class="evaluation-title">${escapeHtml(model.title)}</span> Accessibility Conformance Report</h1>
      Based on ${escapeHtml(catalogData.title)}
      <div class="product-section">
        ${headerWithAnchor("Name of Product/Version", "name-of-product-version", 2)}
        ${escapeHtml(model.product.name)}${model.product.version ? ` ${escapeHtml(model.product.version)}` : ""}
      </div>
      <div class="report-dates-ver-section">
        ${headerWithAnchor("Report Dates and Version", "report-date", 2)}
        <ul>
          <li>Report Date: ${escapeHtml(model.report_date)}</li>
          <li>Last Modified Date: ${escapeHtml(model.last_modified_date)}</li>
          <li>Version: ${escapeHtml(reportFilename(model, true))}</li>
        </ul>
      </div>
      ${model.product.description ? `<div class="description-section">${headerWithAnchor("Product Description", "product-description", 2)}${markdownToHtml(model.product.description)}</div>` : ""}
      <div id="contact-section">
        ${headerWithAnchor("Contact Information", "contact-information", 2)}
        ${headerWithAnchor("Author Information", "author", 3)}
        <ul>
          <li>Name: ${escapeHtml(model.author.name)}</li>
          <li>Company: ${escapeHtml(model.author.company_name)}</li>
          <li>Address: ${escapeHtml(model.author.address)}</li>
          <li>Email: <a href="mailto:${escapeHtml(model.author.email)}">${escapeHtml(model.author.email)}</a></li>
          <li>Phone: ${escapeHtml(model.author.phone)}</li>
          <li>Website: <a href="${escapeHtml(model.author.website)}">${escapeHtml(model.author.website)}</a></li>
        </ul>
        ${headerWithAnchor("Vendor Information", "vendor", 3)}
        <ul>
          <li>Name: ${escapeHtml(model.vendor.name)}</li>
          <li>Company: ${escapeHtml(model.vendor.company_name)}</li>
          <li>Address: ${escapeHtml(model.vendor.address)}</li>
          <li>Email: <a href="mailto:${escapeHtml(model.vendor.email)}">${escapeHtml(model.vendor.email)}</a></li>
          <li>Phone: ${escapeHtml(model.vendor.phone)}</li>
          <li>Website: <a href="${escapeHtml(model.vendor.website)}">${escapeHtml(model.vendor.website)}</a></li>
        </ul>
      </div>
      ${model.notes ? `<div class="notes-section">${headerWithAnchor("Notes", "notes", 2)}${markdownToHtml(model.notes)}</div>` : ""}
      ${model.evaluation_methods_used ? `<div class="eval-section">${headerWithAnchor("Evaluation Methods", "evaluation-methods", 2)}${markdownToHtml(model.evaluation_methods_used)}</div>` : ""}
      <div class="standards-section">
        ${headerWithAnchor("Applicable Standards/Guidelines", "applicable-standards-guidelines", 2)}
        This report covers the degree of conformance for the following accessibility standard/guidelines:
        <table class="applicable-standards-guidelines-table" border="1" style="border-collapse: collapse">
          <thead><tr><th>Standard/Guideline</th><th>Included In Report</th></tr></thead>
          <tbody>${standardsRows}</tbody>
        </table>
      </div>
      <div class="terms-section">
        ${headerWithAnchor("Terms", "terms", 2)}
        The terms used in the Conformance Level information are defined as follows:
        <ul>${catalogData.terms.map((term) => `<li><strong>${escapeHtml(term.label)}</strong>: ${escapeHtml(term.description)}</li>`).join("")}</ul>
      </div>
      ${standardsSections}
      ${model.legal_disclaimer ? `${headerWithAnchor(`Legal Disclaimer${model.vendor.company_name ? ` - ${model.vendor.company_name}` : ""}`, "legal-disclaimer", 2)}${markdownToHtml(model.legal_disclaimer)}` : ""}
      ${model.repository ? `${headerWithAnchor("Repository", "repository", 2)}<a href="${escapeHtml(model.repository)}">${escapeHtml(model.repository)}</a>` : ""}
      ${model.feedback ? `${headerWithAnchor("Feedback", "feedback", 2)}<a href="${escapeHtml(model.feedback)}">${escapeHtml(model.feedback)}</a>` : ""}
      ${headerWithAnchor("Related ACRs", "related-acrs", 2)}
      <ul>${relatedList}</ul>
    </div>
  </main>
  <footer class="usa-footer usa-footer--slim">
    <div class="usa-footer__return-to-top"><div class="grid-container"><a href="#">Return to top</a></div></div>
    <div class="usa-footer__secondary-section"><div class="grid-container"><div class="grid-row grid-gap">
      <div class="grid-col"><a href="https://github.com/GSA/openacr" target="_blank" rel="noopener noreferrer">ACR<span class="usa-sr-only"> (opens in new window)</span></a> is a format maintained by the <a href="https://gsa.gov/" target="_blank" rel="noopener noreferrer">GSA<span class="usa-sr-only"> (opens in new window)</span></a>. The content is the responsibility of the author.</div>
      <div class="grid-col">This content is licensed under a <a href="https://creativecommons.org/licenses/by/4.0/legalcode" target="_blank" rel="noopener noreferrer">${escapeHtml(model.license || "CC-BY-4.0")}<span class="usa-sr-only"> (opens in new window)</span></a>.</div>
    </div></div></div>
  </footer>
</body>
</html>`;
}
